import { bbox } from "@turf/turf";
import { MultiPolygon, Polygon } from "geojson";
import _ from "lodash";
import React, { useCallback, useEffect, useMemo, useRef } from "react";
import Map, { Layer, LayerProps, MapRef, Source } from "react-map-gl/mapbox";

import { type UUID } from "../../../../apis/types/dataLayerTypes";
import { COLOR_BREWER2 } from "../VectorDataLayerUpload/VectorConfiguratorModal";

/**
 * Transparency- used for aerialfarms_std = 0 (unassigned)
 */
const TRANSPARENT_COLOR = "rgba(0,0,0,0)";
const FIELD_BOUNDARY_CSS_VAR = "--secondary-gold-400";
const FIELD_BOUNDARY_FALLBACK_COLOR = "rgba(255, 210, 10, 1)";
const PLOT_BOUNDARY_CSS_VAR = "--accent-alternate-UI-400";
const PLOT_BOUNDARY_FALLBACK_COLOR = "rgba(32, 183, 241, 1)";

const MAP_FITBOUNDS_ANIMATION_MS = 500;
const MAP_FITBOUNDS_PX = 20;

const resolveCssVarColor = (cssVarName: string, fallbackColor: string) => {
	if (typeof window === "undefined" || typeof document === "undefined") {
		return fallbackColor;
	}
	const value = window.getComputedStyle(document.documentElement).getPropertyValue(cssVarName).trim();
	return value || fallbackColor;
};

export interface DataLayerMapProps {
	dataLayerNumZones: number;
	fieldIds: UUID[]; // array of fieldIds to act as `key` for fieldBoundaries and plotBoundaries map layers
	flightIds: UUID[]; // array of flightIds to act as (secondary) `key` for fieldBoundaries and plotBoundaries
	dataLayerFeatures: GeoJSON.FeatureCollection<Polygon | MultiPolygon>; // preview of data layer upload/ zonation
	fieldBoundaries: GeoJSON.Feature<Polygon>[]; // list of selected Feature map layers
	plotBoundaries: GeoJSON.FeatureCollection<Polygon>[]; // list of selected FeatureCollection map layers
}

/**
 * Mapbox component for vector data layers, overlay with field boundaries and plot boundaries.
 */
const DataLayerMap = ({
	dataLayerNumZones,
	fieldIds,
	flightIds,
	dataLayerFeatures,
	fieldBoundaries,
	plotBoundaries,
}: DataLayerMapProps) => {
	const mapRef = useRef<MapRef>(null);
	const fieldBoundaryColor = useMemo(
		() => resolveCssVarColor(FIELD_BOUNDARY_CSS_VAR, FIELD_BOUNDARY_FALLBACK_COLOR),
		[],
	);
	const plotBoundaryColor = useMemo(() => resolveCssVarColor(PLOT_BOUNDARY_CSS_VAR, PLOT_BOUNDARY_FALLBACK_COLOR), []);

	/*
	 * Callbacks
	 */

	// OnLoad handler for mapbox: Sync map bounds with AOI by creating a FeatureCollection from the
	// list of field boundaries.
	const fitMapBoundsToDataLayerFeatures = useCallback(
		(durationMs: number) => {
			if (mapRef?.current) {
				const [minLng, minLat, maxLng, maxLat] = bbox(dataLayerFeatures);
				mapRef.current.fitBounds(
					[
						[minLng, minLat],
						[maxLng, maxLat],
					],
					{
						padding: MAP_FITBOUNDS_PX,
						duration: durationMs,
						animate: durationMs > 0,
						linear: false, // aka fly-to
						essential: true, // ignore reduced-motion settings
					},
				);
			}
		},
		[dataLayerFeatures],
	);

	const fitMapBoundsToSelectedPlotBoundaries = useCallback(() => {
		if (mapRef?.current && plotBoundaries.length > 0) {
			const [minLng, minLat, maxLng, maxLat] = bbox({
				type: "FeatureCollection" as const,
				features: plotBoundaries.flatMap((fc) => fc.features),
			});
			mapRef.current.fitBounds(
				[
					[minLng, minLat],
					[maxLng, maxLat],
				],
				{
					padding: MAP_FITBOUNDS_PX,
					duration: MAP_FITBOUNDS_ANIMATION_MS,
					animate: true,
					linear: false, // aka fly-to
					essential: true, // ignore reduced-motion settings
				},
			);
		}
	}, [plotBoundaries]);

	/*
	 * Effects
	 */

	// Re-fit to AOI when dataLayerFeatures changes
	useEffect(() => {
		fitMapBoundsToDataLayerFeatures(MAP_FITBOUNDS_ANIMATION_MS);
	}, [dataLayerFeatures, fitMapBoundsToDataLayerFeatures]);

	// Re-fit to AOI when selected field + show plots selection changes. By default none are shown.
	useEffect(() => {
		if (plotBoundaries.length > 0) {
			fitMapBoundsToSelectedPlotBoundaries();
		} else {
			fitMapBoundsToDataLayerFeatures(0);
		}
	}, [plotBoundaries, fitMapBoundsToSelectedPlotBoundaries, fitMapBoundsToDataLayerFeatures]);

	/*
	 * Memoization/derived values
	 */

	// Lookup table of categorical colors for the config attribute feature table.
	// Use the stdId (integer value) for indexing lookup.
	const targetAttrColors = useMemo(() => {
		// the colorbrewer2 pallets only have between 3-9 greys
		let numColors = _.min([dataLayerNumZones, 9]) ?? 3;
		numColors = _.max([numColors, 3]) ?? 3;
		const greys = COLOR_BREWER2.Greys[String(numColors) as keyof typeof COLOR_BREWER2.Greys] as string[];
		// prefix with transparent color in position 0
		const pallette = [TRANSPARENT_COLOR].concat(greys);
		return pallette;
	}, [dataLayerNumZones]);

	const dataLayerMapStyle: LayerProps = {
		id: "data-layer",
		type: "fill",
		paint: {
			"fill-color": [
				"match",
				["get", "aerialfarms_std"],
				..._.flatMap(targetAttrColors, (color, i) => [i, color]),
				TRANSPARENT_COLOR,
			],
			"fill-opacity": 0.9,
			"fill-outline-color": fieldBoundaryColor,
		},
	};

	const fieldBoundaryMapStyle: LayerProps = {
		// id: dynamic
		type: "line",
		paint: {
			"line-color": fieldBoundaryColor,
			"line-opacity": 0.9,
		},
	};

	const plotBoundariesMapStyle: LayerProps = {
		// id: dynamic
		type: "line",
		paint: {
			"line-color": plotBoundaryColor,
			"line-opacity": 0.9,
		},
	};

	const uniqueFieldFlightIds = _.zip(fieldIds, flightIds).map(([fieldId, flightId]) => `${fieldId}--${flightId}`);

	/*
	 * Rendering
	 */
	return (
		<Map
			ref={mapRef}
			onLoad={() => fitMapBoundsToDataLayerFeatures(0)}
			projection="mercator"
			mapStyle="mapbox://styles/mapbox/satellite-streets-v12"
			mapboxAccessToken={process.env.MAPBOX_ACCESS_TOKEN}
		>
			<Source type="geojson" data={dataLayerFeatures}>
				<Layer {...dataLayerMapStyle} />
			</Source>
			{fieldBoundaries.map((feature, i) => {
				const uniqueId = uniqueFieldFlightIds[i];
				return (
					<Source type="geojson" data={feature} key={`field-boundary-for-flight-${uniqueId}`}>
						<Layer id={`field-boundary-${uniqueId}`} {...fieldBoundaryMapStyle} />
					</Source>
				);
			})}
			{plotBoundaries.map((featureCollection, i) => {
				const uniqueId = uniqueFieldFlightIds[i];
				return (
					<Source type="geojson" data={featureCollection} key={`plot-boundaries-for-flight-${uniqueId}`}>
						<Layer id={`plot-boundaries-${uniqueId}`} {...plotBoundariesMapStyle} />
					</Source>
				);
			})}
		</Map>
	);
};

export default DataLayerMap;
